import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Users, Eye, MousePointerClick, Globe, Clock, Monitor } from "lucide-react";
import api, { apiErr } from "@/lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

export default function UniqueAnalytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [days, setDays] = useState(30);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use the app's API helper which handles cookie-based auth
      const res = await api.get(`/analytics/stats?days=${days}`);
      setStats(res.data);
    } catch (err) {
      console.error("Analytics error:", err);
      const errMsg = apiErr(err);
      
      // If unauthorized or forbidden, redirect to home
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError("Admin access required");
        setTimeout(() => navigate("/"), 2000);
      } else {
        setError(errMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-amber-500 mx-auto mb-4"></div>
          <p className="text-zinc-400">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="bg-red-950/30 border border-red-800 rounded-lg p-8 max-w-md">
          <h2 className="text-red-400 text-xl font-cinzel mb-2">Access Denied</h2>
          <p className="text-zinc-300">{error}</p>
        </div>
      </div>
    );
  }

  if (!stats) return null;

  const StatCard = ({ icon: Icon, label, value, subtext, color = "amber" }) => (
    <div className={`bg-zinc-900 border border-${color}-900/30 rounded-lg p-6`}>
      <div className="flex items-center gap-3 mb-2">
        <Icon className={`text-${color}-500`} size={24} />
        <h3 className="text-zinc-400 text-sm uppercase tracking-wide">{label}</h3>
      </div>
      <div className={`text-3xl font-bold text-${color}-400 mb-1`}>{value.toLocaleString()}</div>
      {subtext && <p className="text-zinc-500 text-sm">{subtext}</p>}
    </div>
  );

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-cinzel text-4xl text-amber-300 uppercase tracking-wide mb-2 flex items-center gap-3">
            <BarChart size={36} />
            Analytics Dashboard
          </h1>
          <p className="text-zinc-400">Site traffic and visitor statistics</p>
        </div>

        {/* Time Range Selector */}
        <div className="mb-6 flex gap-2">
          {[7, 14, 30, 60, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`px-4 py-2 rounded border transition-all ${
                days === d
                  ? "bg-amber-900 border-amber-700 text-amber-200"
                  : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:border-amber-900"
              }`}
            >
              {d} days
            </button>
          ))}
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard
            icon={Eye}
            label="Total Page Views"
            value={stats.total_page_views}
            subtext={`Last ${days} days`}
            color="blue"
          />
          <StatCard
            icon={Users}
            label="Unique Visitors"
            value={stats.unique_visitors}
            subtext="By IP address"
            color="green"
          />
          <StatCard
            icon={MousePointerClick}
            label="Logged-In Visitors"
            value={stats.logged_in_visitors}
            subtext="Authenticated users"
            color="amber"
          />
          <StatCard
            icon={Globe}
            label="Anonymous Visitors"
            value={stats.anonymous_visitors}
            subtext="Not logged in"
            color="purple"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Top Referrers */}
          <div className="bg-zinc-900 border border-amber-900/30 rounded-lg p-6">
            <h2 className="font-cinzel text-xl text-amber-300 mb-4 flex items-center gap-2">
              <Globe size={20} />
              Top Referrers
            </h2>
            <div className="space-y-2">
              {stats.top_referrers.length === 0 ? (
                <p className="text-zinc-500 text-sm">No referrer data yet</p>
              ) : (
                stats.top_referrers.map((ref, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded p-3"
                  >
                    <span className="text-zinc-300 text-sm truncate flex-1">
                      {ref.referrer === "direct" ? (
                        <span className="text-amber-500 font-semibold">Direct Traffic</span>
                      ) : (
                        ref.referrer
                      )}
                    </span>
                    <span className="text-amber-400 font-semibold ml-2">{ref.count}</span>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Top Pages */}
          <div className="bg-zinc-900 border border-amber-900/30 rounded-lg p-6">
            <h2 className="font-cinzel text-xl text-amber-300 mb-4 flex items-center gap-2">
              <MousePointerClick size={20} />
              Top Pages
            </h2>
            <div className="space-y-2">
              {stats.top_pages.map((page, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between bg-zinc-950 border border-zinc-800 rounded p-3"
                >
                  <span className="text-zinc-300 text-sm truncate flex-1">{page.page}</span>
                  <span className="text-amber-400 font-semibold ml-2">{page.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Daily Traffic Chart */}
        <div className="bg-zinc-900 border border-amber-900/30 rounded-lg p-6 mb-6">
          <h2 className="font-cinzel text-xl text-amber-300 mb-4 flex items-center gap-2">
            <BarChart size={20} />
            Daily Traffic
          </h2>
          <div className="space-y-3">
            {stats.daily_traffic.length === 0 ? (
              <p className="text-zinc-500 text-sm">No traffic data yet</p>
            ) : (
              stats.daily_traffic.slice(-14).map((day) => {
                const maxViews = Math.max(...stats.daily_traffic.map((d) => d.views));
                const widthPercent = maxViews > 0 ? (day.views / maxViews) * 100 : 0;
                
                return (
                  <div key={day.date} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-400">{day.date}</span>
                      <span className="text-zinc-300">
                        {day.views} views • {day.unique_visitors} unique
                      </span>
                    </div>
                    <div className="h-8 bg-zinc-950 rounded overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-amber-900 to-amber-600 flex items-center justify-between px-3"
                        style={{ width: `${Math.max(widthPercent, 2)}%` }}
                      >
                        <span className="text-xs text-amber-100">
                          {day.logged_in > 0 && `${day.logged_in} logged`}
                        </span>
                        <span className="text-xs text-amber-200">
                          {day.anonymous > 0 && `${day.anonymous} anon`}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Browser Stats */}
          <div className="bg-zinc-900 border border-amber-900/30 rounded-lg p-6">
            <h2 className="font-cinzel text-xl text-amber-300 mb-4 flex items-center gap-2">
              <Monitor size={20} />
              Browsers
            </h2>
            <div className="space-y-2">
              {stats.browser_stats.map((browser, idx) => {
                const total = stats.browser_stats.reduce((sum, b) => sum + b.count, 0);
                const percent = total > 0 ? ((browser.count / total) * 100).toFixed(1) : 0;
                
                return (
                  <div key={idx} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-zinc-300">{browser.browser}</span>
                      <span className="text-amber-400">{browser.count} ({percent}%)</span>
                    </div>
                    <div className="h-2 bg-zinc-950 rounded overflow-hidden">
                      <div
                        className="h-full bg-amber-600"
                        style={{ width: `${percent}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Hourly Distribution */}
          <div className="bg-zinc-900 border border-amber-900/30 rounded-lg p-6">
            <h2 className="font-cinzel text-xl text-amber-300 mb-4 flex items-center gap-2">
              <Clock size={20} />
              Peak Hours (UTC)
            </h2>
            <div className="grid grid-cols-6 gap-2">
              {stats.hourly_distribution.map((hour) => {
                const maxCount = Math.max(...stats.hourly_distribution.map((h) => h.count));
                const heightPercent = maxCount > 0 ? (hour.count / maxCount) * 100 : 0;
                
                return (
                  <div key={hour.hour} className="flex flex-col items-center">
                    <div className="h-24 w-full bg-zinc-950 rounded relative flex items-end justify-center">
                      <div
                        className="w-full bg-amber-700 rounded-t"
                        style={{ height: `${Math.max(heightPercent, 3)}%` }}
                        title={`${hour.count} views`}
                      />
                    </div>
                    <span className="text-xs text-zinc-500 mt-1">
                      {hour.hour.toString().padStart(2, "0")}:00
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
